import { FeatureFlags, Translated } from '../../types/Global'
import { Document } from '@contentful/rich-text-types'
import {
  CategoryPageSection,
  ProductGridType,
  ProductLaneType
} from '../../types/TemplateConfiguration'
import { BFFPageData } from '../../services/serviceClients/BffClient'
import { MSXAttibuteValue } from '../../types/Msx'
import { ShortMsxMpv } from '../../types/ShortMsxMpv'
import { CalloutProperties, CalloutSkin, CalloutTagType, CalloutVariant } from '../../types/Callout'
import { ReviewsResponse } from '../../types/Contentful/ContentfulTypes'
import { createModelFromAutomatedExperience } from '../../components/AutoGenerate/automatedProductLaneSectionCreator'
import { AutomatedExperience } from '../../components/AutoGenerate/types/AutomatedExperience'
import { ProductLaneModel } from '../../components/AutoGenerate/types/ProductLaneModel'
import { shouldUseAutomatedExperience } from '../../components/AutoGenerate/automatedSectionUtil'
import { ProductVariantHandler } from './ProductVariantHandler'
import { DisplayConfiguration } from './DisplayConfiguration'
import { DescriptionFormatter } from './DescriptionFormatter'
import { LaneModelContext } from './types'
import { LaneModelBuilder, GridTileBuilder, MpvTileBuilder } from '../builders'

export interface ProductTileCollectionModel {
  title?: Translated
  subtitle?: Translated
  tiles: ProductTileModel[]
}

export interface ProductTileModel {
  name?: string
  description?: string
  image?: {
    altTextOverride?: string
    url?: string
  }
  callout?: string
  calloutColor?: CalloutSkin
  calloutVariant?: CalloutVariant
  productTileLongDescription?: Document
  linkUrl?: string
  isOutOfStock?: boolean
  mpvId?: string
  productId?: string
  showPrice?: boolean
  associatedPageAvailable?: boolean
  colorInformation?: MSXAttibuteValue[]
  product?: { contentful_id: string; template?: ProductTileV2 }
  version?: number
}

export interface CalloutDataType {
  skin?: CalloutSkin
  variant?: CalloutVariant
  text?: {
    value?: string
  }
  nonLocalizedValue?: CalloutTagType
}

export interface MCSSelectedProductVariants {
  Quantity?: string // Keep as 'Quantity' (uppercase) – this matches the key returned by MCS
  [key: string]: string
}

export interface AllAvailableTags {
  default: CalloutProperties | null
  below_tile: CalloutProperties | null
}

export interface ProductTileV2 {
  calloutDetails?: CalloutDataType
  productKey?: string
  productsList?: {
    selectedProduct?: {
      mpvId?: string
    }
    selectedProductVariants?: MCSSelectedProductVariants
  }
  category?: {
    selectedCategory?: {
      mpvId: string
      name: {
        value: string
      }
      link?: {
        href?: string
      }
    }
  }
  secondaryLink: {
    link?: { href?: string; text?: string }
    label?: {
      value?: string
    }
  }
  tileDetails?: ProductTileV2TileDetails
  allAvailableTags?: AllAvailableTags
}

interface ProductTileV2TileDetails {
  image?: {
    cloudinaryImage?: {
      src?: string
      altTextOverride?: string
    }[]
  }
  description?: { value?: string }
  formattedDescription?: { value?: string }
  header?: {
    value?: string
  }
  urlText?: { value?: string }
  link?: {
    href?: string
    text?: string
    linkType?: string
  }
}

/**
 * Main entry point for product tile collection utilities
 */
export default class ProductTileCollectionUtil {
  /**
   * Builds a ProductTileCollectionModel for grid sections
   */
  public static getModel(
    section: ProductGridType,
    pageDataFromBFF: BFFPageData
  ): ProductTileCollectionModel {
    const builder = new GridTileBuilder(pageDataFromBFF)
    const tiles: ProductTileModel[] = []

    for (const item of section.items) {
      const tile = builder.buildTile(item)
      if (tile) tiles.push(tile)
    }

    const result: ProductTileCollectionModel = { tiles }
    if (section.title !== undefined) result.title = section.title
    if (section.subtitle !== undefined) result.subtitle = section.subtitle
    return result
  }

  /**
   * Gets maximum color swatch count for desktop
   * @deprecated Use DisplayConfiguration.getMaxColorSwatchCountDesktop instead
   */
  public static getMaxColorSwatchCountDesktop(tilesCount: number, sideBarEnabled: boolean): number {
    return DisplayConfiguration.getMaxColorSwatchCountDesktop(tilesCount, sideBarEnabled)
  }

  /**
   * Gets number of colors to display based on screen size
   * @deprecated Use DisplayConfiguration.getNumberOfColorsToDisplay instead
   */
  public static getNumberOfColorsToDisplay(screenClass: string, colorSwatchCount: number): number {
    return DisplayConfiguration.getNumberOfColorsToDisplay(screenClass, colorSwatchCount)
  }

  /**
   * Gets number of product tiles per row
   * @deprecated Use DisplayConfiguration.getNumberOfTilesPerRow instead
   */
  public static getNumberOfProductTilesPerRow(
    sidebarEnabled: boolean,
    sectionType: string,
    tilesCount: number
  ): number {
    return DisplayConfiguration.getNumberOfTilesPerRow(sidebarEnabled, sectionType, tilesCount)
  }

  /**
   * Gets formatted description
   * @deprecated Use DescriptionFormatter.format instead
   */
  public static getFormattedDescription(
    hideProductDescription: boolean | undefined,
    formattedDescription: string | undefined
  ): string {
    return DescriptionFormatter.format(hideProductDescription, formattedDescription)
  }

  /**
   * Gets product variant selections, excluding Quantity
   * @deprecated Use ProductVariantHandler.getSelections instead
   */
  public static getSelectionsFromProductVariants(
    variants: MCSSelectedProductVariants | undefined
  ): Record<string, string> {
    return ProductVariantHandler.getSelections(variants)
  }

  /**
   * Builds a ProductTileModel from MPV data
   * @deprecated Use MpvTileBuilder.buildFromMpv instead
   */
  public static getTileV2FromMpv(
    mpvId: string,
    serverSideMpvData: ShortMsxMpv | undefined
  ): ProductTileModel {
    return MpvTileBuilder.buildFromMpv(mpvId, serverSideMpvData)
  }

  /**
   * Creates a ProductLaneModel from section configuration
   *
   * Handles both automated and manually generated experiences.
   *
   * Note: The public method signature accepts all parameters for backward compatibility.
   * Internally uses a context pattern to comply with parameter limits.
   */
  public static createProductLaneModel(
    section: ProductLaneType,
    sectionNumber: number,
    nextSection: CategoryPageSection | undefined,
    reviewsData: ReviewsResponse | undefined,
    enableCampaignCallouts: boolean | undefined,
    pageDataFromBFF: BFFPageData,
    automatedExperience?: AutomatedExperience,
    featureFlags?: FeatureFlags
  ): ProductLaneModel | undefined {
    const context: LaneModelContext = {
      section,
      pageDataFromBFF,
      options: {
        sectionNumber,
        nextSection,
        reviewsData,
        enableCampaignCallouts,
        automatedExperience,
        featureFlags
      }
    }
    return this.buildProductLaneModel(context)
  }

  private static buildProductLaneModel(context: LaneModelContext): ProductLaneModel | undefined {
    const { sectionNumber, nextSection, automatedExperience, featureFlags } = context.options

    const matchingAutomatedSection = automatedExperience?.sections?.find(
      (automatedSection) => automatedSection.sectionId === context.section.sectionId
    )

    const shouldDisplayAutomatedContent =
      matchingAutomatedSection &&
      context.section.automationEnabled &&
      shouldUseAutomatedExperience(matchingAutomatedSection, featureFlags)

    if (shouldDisplayAutomatedContent) {
      return createModelFromAutomatedExperience(
        matchingAutomatedSection,
        sectionNumber,
        nextSection
      )
    }

    return LaneModelBuilder.build(
      context.section,
      sectionNumber,
      nextSection,
      context.options.reviewsData,
      context.options.enableCampaignCallouts,
      context.pageDataFromBFF
    )
  }
}
